import React, { useState, useEffect } from 'react';

export default function FiveCrownsTVDisplay() {
  const rounds = ['3s', '4s', '5s', '6s', '7s', '8s', '9s', '10s', 'Js', 'Qs', 'Ks'];

  const [playerCount, setPlayerCount] = useState(2);
  const [players, setPlayers] = useState(['Player 1', 'Player 2']);
  const [scores, setScores] = useState([Array(11).fill(''), Array(11).fill('')]);

  // Load and sync data from localStorage
  useEffect(() => {
    const loadData = () => {
      try {
        const stored = localStorage.getItem('five-crowns-v9');
        if (stored) {
          const data = JSON.parse(stored);
          if (data.players) setPlayers(data.players);
          if (data.scores) setScores(data.scores);
          if (data.playerCount) setPlayerCount(data.playerCount);
        }
      } catch (err) {
        console.log('Error loading data:', err);
      }
    };

    // Initial load
    loadData();

    // Listen for storage changes (real-time sync)
    const handleStorageChange = (e) => {
      if (e.key === 'five-crowns-v9') {
        loadData();
      }
    };

    window.addEventListener('storage', handleStorageChange);

    // Also poll for changes (backup for same-tab updates)
    const interval = setInterval(loadData, 500);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
    };
  }, []);

  const getTotal = (playerIndex) => {
    return scores[playerIndex]?.reduce((sum, score) => sum + (parseInt(score) || 0), 0) || 0;
  };

  const hasAnyScores = () => {
    return players.slice(0, playerCount).some((_, i) => scores[i]?.some(s => s !== ''));
  };

  const isGameComplete = () => {
    return players.slice(0, playerCount).every((_, p) =>
      rounds.every((_, r) => scores[p]?.[r] !== '')
    );
  };

  const getLeaderboard = () => {
    const playerData = players
      .slice(0, playerCount)
      .map((name, i) => ({ name, total: getTotal(i), index: i }));

    if (!hasAnyScores()) {
      return playerData;
    }

    return playerData.sort((a, b) => a.total - b.total);
  };

  const getWinners = () => {
    if (!isGameComplete()) return [];
    const leaderboard = getLeaderboard();
    const lowestScore = leaderboard[0].total;
    return leaderboard.filter(p => p.total === lowestScore);
  };

  const getCurrentRound = () => {
    for (let r = 0; r < rounds.length; r++) {
      const allFilled = players.slice(0, playerCount).every((_, p) => scores[p]?.[r] !== '');
      if (!allFilled) return r;
    }
    return rounds.length;
  };

  const getMedal = (rank) => {
    if (rank === 0) return '🥇';
    if (rank === 1) return '🥈';
    if (rank === 2) return '🥉';
    return `#${rank + 1}`;
  };

  const getRank = (playerIndex, leaderboard) => {
    if (!hasAnyScores()) return null;
    const playerTotal = getTotal(playerIndex);
    let rank = 0;
    let prevTotal = null;

    for (let i = 0; i < leaderboard.length; i++) {
      if (leaderboard[i].total !== prevTotal) {
        rank = i;
        prevTotal = leaderboard[i].total;
      }
      if (leaderboard[i].index === playerIndex) {
        return rank;
      }
    }
    return 0;
  };

  const leaderboard = getLeaderboard();
  const currentRound = getCurrentRound();
  const currentDealer = currentRound % playerCount;
  const gameHasScores = hasAnyScores();
  const gameComplete = isGameComplete();
  const winners = getWinners();

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-900 via-emerald-950 to-black p-8 flex">
      {/* Main Scorecard - Left Side (70%) */}
      <div className="flex-1 pr-6 flex flex-col">
        {/* Header */}
        <div className="text-center mb-6">
          <h1 className="text-6xl font-bold text-amber-400 mb-2">👑 Five Crowns 👑</h1>
          <p className="text-emerald-300 text-2xl">
            {gameComplete ? '🎉 Game Complete!' : `Round: ${rounds[currentRound]} • 🃏 ${players[currentDealer]} dealing`}
          </p>
        </div>

        {/* Winner Banner */}
        {gameComplete && winners.length > 0 && (
          <div className="bg-gradient-to-r from-amber-500 via-yellow-400 to-amber-500 rounded-2xl p-6 mb-6 text-center animate-pulse">
            <div className="text-5xl mb-2">👑🎉👑</div>
            {winners.length === 1 ? (
              <>
                <div className="text-emerald-950 font-bold text-4xl">{winners[0].name} WINS!</div>
                <div className="text-emerald-900 text-2xl">Final Score: {winners[0].total} points</div>
              </>
            ) : (
              <>
                <div className="text-emerald-950 font-bold text-4xl">TIE GAME!</div>
                <div className="text-emerald-900 text-2xl">
                  {winners.map(w => w.name).join(' & ')} tied with {winners[0].total} points!
                </div>
              </>
            )}
          </div>
        )}

        {/* Score Table */}
        <div className="flex-1 overflow-auto rounded-2xl bg-emerald-950/50 border border-emerald-700/50">
          <table className="text-xl border-collapse w-auto">
            <thead className="sticky top-0 z-10">
              <tr className="bg-emerald-900">
                <th className="p-4 text-amber-400 text-2xl font-bold text-left w-20">Wild</th>
                {players.slice(0, playerCount).map((player, i) => {
                  const rank = getRank(i, leaderboard);
                  const isDealer = currentDealer === i && !gameComplete;
                  const isLeader = rank === 0 && gameHasScores;
                  const isWinner = winners.some(w => w.index === i);
                  return (
                    <th
                      key={i}
                      className={`p-4 text-center min-w-[120px] max-w-[180px] transition-all ${
                        isDealer ? 'bg-amber-500/30' : ''
                      } ${isWinner && gameComplete ? 'bg-amber-500/40' : ''}`}
                    >
                      <span className={`text-xl font-bold ${
                        isWinner && gameComplete ? 'text-amber-300' :
                        isDealer ? 'text-amber-300' :
                        isLeader ? 'text-amber-300' : 'text-white'
                      }`}>
                        {isWinner && gameComplete ? '👑 ' : isDealer ? '🃏 ' : ''}{isLeader && !gameComplete ? '👑 ' : ''}{player}
                      </span>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {rounds.map((round, ri) => (
                <tr
                  key={round}
                  className={`border-t border-emerald-700/30 ${
                    ri === currentRound && !gameComplete ? 'bg-emerald-700/30' : ''
                  }`}
                >
                  <td className={`p-4 font-bold text-xl text-left ${
                    ri === currentRound && !gameComplete ? 'text-amber-200 bg-emerald-800/50' : 'text-amber-300'
                  }`}>
                    {ri === currentRound && !gameComplete ? '▶ ' : ''}{round}
                  </td>
                  {players.slice(0, playerCount).map((_, pi) => {
                    const scoreValue = scores[pi]?.[ri] || '';
                    const isZero = scoreValue === '0';
                    const isWinner = winners.some(w => w.index === pi);
                    return (
                      <td
                        key={pi}
                        className={`p-4 text-center text-2xl font-mono ${
                          isWinner && gameComplete ? 'bg-amber-500/10' : ''
                        }`}
                      >
                        <span className={isZero ? 'text-green-400 font-bold' : 'text-white'}>
                          {scoreValue || '–'}
                        </span>
                      </td>
                    );
                  })}
                </tr>
              ))}
              {/* Totals Row */}
              <tr className="bg-emerald-900 font-bold border-t-4 border-emerald-600 sticky bottom-0">
                <td className="p-4 text-amber-400 text-2xl">TOTAL</td>
                {players.slice(0, playerCount).map((_, pi) => {
                  const rank = getRank(pi, leaderboard);
                  const isLeader = rank === 0 && gameHasScores;
                  const isWinner = winners.some(w => w.index === pi);
                  return (
                    <td
                      key={pi}
                      className={`p-4 text-center text-3xl ${
                        isWinner && gameComplete ? 'bg-amber-500/20' : ''
                      } ${isLeader ? 'text-amber-300' : 'text-white'}`}
                    >
                      {getTotal(pi)}
                    </td>
                  );
                })}
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Leaderboard - Right Side (30%) */}
      <div className="w-96 flex flex-col">
        <div className="bg-emerald-950/80 rounded-2xl p-6 border border-emerald-700/50 flex-1 flex flex-col">
          <h2 className="text-4xl font-bold text-amber-400 mb-6 text-center">🏆 STANDINGS</h2>

          {/* Top 3 Podium - Large */}
          <div className="space-y-4 mb-6">
            {leaderboard.slice(0, 3).map((player, i) => {
              const rank = i;
              const isWinner = winners.some(w => w.index === player.index);

              return (
                <div
                  key={player.index}
                  className={`flex items-center justify-between px-6 py-5 rounded-xl transition-all ${
                    !gameHasScores ? 'bg-emerald-800/30' :
                    isWinner && gameComplete ? 'bg-amber-500/40 border-2 border-amber-400 animate-pulse' :
                    rank === 0 ? 'bg-amber-500/30 border border-amber-500/50' :
                    rank === 1 ? 'bg-gray-400/20 border border-gray-400/30' :
                    rank === 2 ? 'bg-orange-600/20 border border-orange-500/30' :
                    'bg-emerald-800/30'
                  }`}
                >
                  <div className="flex items-center gap-4">
                    <span className="text-5xl">
                      {!gameHasScores ? `${i + 1}.` : isWinner && gameComplete ? '👑' : getMedal(rank)}
                    </span>
                    <span className={`text-3xl font-bold ${
                      rank === 0 && gameHasScores ? 'text-amber-300' : 'text-white'
                    }`}>
                      {player.name}
                    </span>
                  </div>
                  <span className={`text-4xl font-bold font-mono ${
                    rank === 0 && gameHasScores ? 'text-amber-300' : 'text-white'
                  }`}>
                    {gameHasScores ? player.total : '–'}
                  </span>
                </div>
              );
            })}
          </div>

          {/* 4th place and beyond - Scrollable, smaller */}
          {leaderboard.length > 3 && (
            <div className="flex-1 overflow-auto">
              <div className="space-y-2">
                {leaderboard.slice(3).map((player, i) => (
                  <div
                    key={player.index}
                    className="flex items-center justify-between px-4 py-3 rounded-lg bg-emerald-800/30"
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-2xl text-emerald-400">#{i + 4}</span>
                      <span className="text-xl text-white">{player.name}</span>
                    </div>
                    <span className="text-2xl font-mono text-white">
                      {gameHasScores ? player.total : '–'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="mt-4 text-center text-emerald-400 text-lg">
          J=11 • Q=12 • K=13 • Joker=50
        </div>
      </div>
    </div>
  );
}
